import React from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {
  Accordion,
  ListSelect,
  Loader,
  ProjectSharingSuggestions,
  SharePanel,
  SocialShareButtons
} from '@communityshare/ui-shared';
import {PayloadStates} from '@lore/utils';
import {useConnect} from '@lore/query-connect';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import ProjectLinkPreview from './ProjectLinkPreview';
import useProjectShareActions from '@/features/share-project/hooks/useProjectShareActions';

ProjectShareDialog.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number,
    state: PropTypes.string,
    data: PropTypes.shape({
      title: PropTypes.string,
      networkId: PropTypes.number
    })
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  fromManage: PropTypes.bool
};

export default function ProjectShareDialog(props) {
  const {project, onClose, fromManage = false} = props;
  const {t} = useTranslation();
  const shareActions = useProjectShareActions(project);
  const network = useConnect('network.byId', {
    id: project?.data?.networkId
  }, {enabled: !!project?.data?.networkId});

  if (project.state === PayloadStates.FETCHING || network.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader/>
      </DialogTemplate>
    );
  }

  const title = project.data.title || 'INCOMPLETE';
  const brand = network?.data?.name || t('project_share.brand');

  return (
    <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
      <CloseDialogButton onClick={onClose}/>
      <SharePanel
        breadcrumbParent={fromManage ? t('project_share.breadcrumb_parent') : null}
        breadcrumbAction={t('project_share.breadcrumb_action')}
        backLabel={t('project_share.back_to_project')}
        onBack={onClose}
        title={t('project_share.title')}
        subtitle={t('project_share.subtitle')}
        selectLabel={t('project_share.field_label')}
        select={
          <ListSelect
            value={project}
            options={[project.data]}
            disabled={true}
            getOptionLabel={() => project.data.title || 'INCOMPLETE'}
            getOptionKey={() => project.id}
            className="bg-white"
            onChange={() => {}}
          />
        }
        calloutBold={t('project_share.callout_bold')}
        calloutBody={t('project_share.callout_body')}
        suggestions={
          <Accordion>
            <ProjectSharingSuggestions
              options={[
                {
                  key: 'find_partners',
                  label: t('project_share.option_find_partners'),
                  template: t('project_share.template_find_partners', {guidingQuestion: title})
                },
                {
                  key: 'spread_word',
                  label: t('project_share.option_spread_word', {brand}),
                  template: t('project_share.template_spread_word', {brand})
                }
              ]}
              examples={[
                {id: 'example-1', text: t('project_share.example_1')},
                {id: 'example-2', text: t('project_share.example_2')},
              ]}
              helpTitle={t('project_share.help_title')}
              viewSuggestionsLabel={t('project_share.view_suggestions')}
              helpQuestion={t('project_share.help_question')}
              templateLabel={t('project_share.template_label')}
              resetTemplateLabel={t('project_share.reset_template')}
              copyLabel={t('project_share.copy_text')}
              copiedLabel={t('project_share.copied')}
              seeExamplesLabel={t('project_share.see_examples')}
            />
          </Accordion>
        }
        shareActions={
          <SocialShareButtons
            {...shareActions}
            copiedLinkLabel={t('controls.Copied')}
            copyLinkLabel={t('controls.Copy link')}
          />
        }
      >
        {/* key remounts the preview on project change so project-specific
            state (selected image, default-save guard) resets. */}
        <ProjectLinkPreview key={project.id} project={project}/>
      </SharePanel>
    </DialogTemplate>
  );
}
